import type { TProcessingStage } from "./pipeline-progress"

/**
 * DAG step ids (backend `pipeline/steps/*`) that belong to the "identifying
 * speakers" phase of the finalize timeline. Everything else in the finalize
 * graph is "analyzing" for the coarse, monotonic stage used by the progress
 * weight math — a late step must not drag that stage backwards.
 *
 * The overlay does not show these ids or their `impl.name`. `friendlyStepForId`
 * is the user-facing bucket (saving audio / transcript / voices / summary).
 */
const SPEAKER_STEP_IDS: ReadonlySet<string> = new Set([
  "cross-session",
  "voiceprint-match",
  "speaker-resolution",
  "speaker-enrollment",
])

/** Speaker work that is not in the coarse set above, but is still "telling voices apart". */
const VOICE_STEP_IDS: ReadonlySet<string> = new Set([
  "speaker-channel-merge",
  "speaker-suggestion-fusion",
  "speaker-label-check",
  "speaker-signal-aggregator",
])

/** Steps that write the transcript the user will read. */
const TRANSCRIPT_STEP_IDS: ReadonlySet<string> = new Set(["clean-transcript"])

/** Steps that store or compress the recording. */
const AUDIO_SAVE_STEP_IDS: ReadonlySet<string> = new Set(["audio-compress"])

/** Which finalize stage a DAG step advances — for the weighted progress math.
 * Speaker steps highlight "identifying speakers"; everything else is "analyzing"
 * so the bar never rewinds when a later bookkeeping step runs. */
export function stepStage(ref: string): TProcessingStage {
  return SPEAKER_STEP_IDS.has(ref) ? "identifying_speakers" : "analyzing"
}

/**
 * Friendly finalize step for a backend step id. Unknown ids land on "analyzing".
 * For Full that is the summary. For Lite, that step is the title.
 * Never returns a display name; callers render the step's own label.
 */
export function friendlyStepForId(ref: string): TProcessingStage {
  if (SPEAKER_STEP_IDS.has(ref) || VOICE_STEP_IDS.has(ref)) return "identifying_speakers"
  if (TRANSCRIPT_STEP_IDS.has(ref)) return "transcribing"
  if (AUDIO_SAVE_STEP_IDS.has(ref)) return "uploading"
  return "analyzing"
}

/**
 * Relative wall-clock cost of each finalize step, used to weight the progress
 * bar so it reflects TIME, not step count. `final-analysis` (the big LLM
 * summary) dominates; the cheap/instant steps (and the whole skipped Voxcanon
 * branch) barely move the bar. Without this, ~a dozen quick/skipped steps shoot
 * the bar to ~90% while the summary — the real wait — is still running.
 */
const STEP_WEIGHTS: Record<string, number> = {
  "final-analysis": 6,
  "progressive-summary": 2,
  "cluster-chapters": 2,
  "speaker-resolution": 2,
}

/** Weight for a step ref (default 1). */
export function stepWeight(ref: string): number {
  return STEP_WEIGHTS[ref] ?? 1
}
